import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import mime from 'mime';
import * as path from 'path-browserify';
import { Download, DownloadState } from './Download';

export const update = 'update';

export class DownloadEvent extends Event {
	public constructor(type: typeof update, public readonly download: Download) {
		super(type);
	}
}

export type DownloadListener = (event: DownloadEvent) => void;

export class DownloadQueue {
	private constructor() {}

	public static get singleton(): DownloadQueue {
		return (this._singleton = this._singleton ?? new DownloadQueue());
	}

	private static _singleton: DownloadQueue | undefined;

	public addEventListener(
		type: typeof update,
		listener: DownloadListener,
		options?: boolean | AddEventListenerOptions,
	): DownloadListener {
		this._eventTarget.addEventListener(type, listener, options);
		return listener;
	}

	public removeEventListener(
		type: typeof update,
		listener: DownloadListener,
		options?: boolean | AddEventListenerOptions,
	): void {
		this._eventTarget.removeEventListener(type, listener, options);
	}

	public async enqueue(
		params: Pick<Download, 'fileNameFromClient' | 'id' | 'label'> & {
			urlPromise: Promise<string> | string;
		},
	): Promise<Download> {
		const partialDownload: Omit<Download, 'state' | 'url'> = {
			fileName: params.fileNameFromClient,
			fileNameFromClient: params.fileNameFromClient,
			id: params.id,
			label: params.label,
			progress: undefined,
		};
		const download = partialDownload as Download;
		this._downloads.set(download.id, download);

		download.state = DownloadState.preparing;
		this.dispatchUpdate(download);

		try {
			download.url = await params.urlPromise;
			this.updateFileNameFromUrl(download);

			download.state = DownloadState.downloading;
			this.dispatchUpdate(download);

			await this.performDownload(download);

			download.state = DownloadState.downloaded;
			this.dispatchUpdate(download);
		} catch (error) {
			download.state = DownloadState.failed;
			this.dispatchUpdate(download);
			throw error;
		}

		return download;
	}

	public get(downloadId: unknown): Download | undefined {
		const download: Download | undefined = this._downloads.get(downloadId);
		const clonedDownload: Download | undefined = download && { ...download };
		return clonedDownload;
	}

	private async performDownload(download: Download): Promise<void> {
		const response: AxiosResponse<any> = await axios({
			method: 'GET',
			url: download.url,
			responseType: 'blob', // important
			skipAuth: true,
			onDownloadProgress: (event: ProgressEvent): void => {
				if (event.lengthComputable) {
					download.progress = (event.loaded * 100) / event.total;
					this.dispatchUpdate(download);
				}
			},
		} as AxiosRequestConfig);

		// Update `progress`.
		let updated: boolean = false;
		if (download.progress !== 100) {
			download.progress = 100;
			updated = true;
		}

		// Update `contentType`.
		const contentType: string | undefined = response.headers['content-type'];
		updated = updated && contentType !== download.contentType;
		download.contentType = contentType;

		// Update `fileNameFromContentDisposition`.
		const fileNameFromContentDisposition: string | undefined = ((): string | undefined => {
			const contentDisposition: string | undefined = response.headers['content-disposition'];
			if (contentDisposition) {
				const match: RegExpMatchArray | null = contentDisposition.match(/filename="(.+)"/);
				if (match?.length === 2) {
					return match[1];
				}
			}
			return undefined;
		})();
		updated =
			updated || fileNameFromContentDisposition !== download.fileNameFromContentDisposition;
		download.fileNameFromContentDisposition = fileNameFromContentDisposition;

		// Update `fileName`
		if (download.fileNameFromContentDisposition) {
			updated = updated || this.updateFileNameFromContentDisposition(download);
		} else if (download.contentType) {
			updated = updated || this.updateFileNameFromContentType(download);
		}

		// If one or more of the above updates actually changed something, the notify listeners.
		if (updated) {
			this.dispatchUpdate(download);
		}

		// Create a temporary anchor element to act as the trigger for downloading the blob to
		// disk.
		const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
		const blobUrl: string = URL.createObjectURL(blob);

		const anchor: HTMLAnchorElement = window.document.createElement('a');
		anchor.href = blobUrl;
		anchor.style.display = 'none';
		anchor.setAttribute('download', download.fileName ?? '');
		window.document.body.appendChild(anchor);

		// Simulate a click to activate the download and then clean up afterwards.
		try {
			anchor.click();
		} finally {
			anchor.remove();
			URL.revokeObjectURL(blobUrl);
		}
	}

	private updateFileNameFromContentType(download: Download): boolean {
		if (download.contentType && download.contentType !== 'application/octet-stream') {
			const mimeExtensionWithoutSeperator: string = mime.getExtension(download.contentType);
			if (mimeExtensionWithoutSeperator) {
				const fileName: string = `${
					path.parse(download.fileNameFromClient).name
				}.${mimeExtensionWithoutSeperator}`;
				if (fileName !== download.fileName) {
					download.fileName = fileName;
					return true;
				}
			}
		}

		return false;
	}

	private updateFileNameFromContentDisposition(download: Download): boolean {
		if (download.fileNameFromContentDisposition) {
			if (download.fileNameFromContentDisposition !== download.fileName) {
				download.fileName = download.fileNameFromContentDisposition;
				return true;
			}
		}

		return false;
	}

	private updateFileNameFromUrl(download: Download): boolean {
		if (download.url) {
			const urlExtensionWithSeparator: string = path.parse(new URL(download.url).pathname)
				.ext;
			if (urlExtensionWithSeparator) {
				const fileName: string = `${
					path.parse(download.fileNameFromClient).name
				}${urlExtensionWithSeparator}`;
				if (fileName !== download.fileName) {
					download.fileName = fileName;
					return true;
				}
			}
		}

		return false;
	}

	private dispatchUpdate(download: Download): void {
		const clonedDownload: Download = { ...download };
		const event = new DownloadEvent(update, clonedDownload);
		this._eventTarget.dispatchEvent(event);
	}

	private readonly _downloads = new Map<unknown, Download>();
	private _eventTarget = new EventTarget();
}
